

// const BASE_URL = "https://staging.pteshstudios.com";

import { useState, useEffect, useRef } from 'react';
import './contact.css';
import './homepageEditor.css'; // adjust path as needed
import Avatar from '../../Images/image14.png';

const API_BASE      = "http://localhost:5000/api/contact";
const UPLOAD_URL    = "http://localhost:5000/api/upload";
const SERVER_ORIGIN = "http://localhost:5000";

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${SERVER_ORIGIN}${url}`;
};

const DEFAULT_DATA = {
  heroImage: "",
  formTitle: "Send Message",
  offices: [
    {
      name: "Lagos Office",
      address: "12 Saka Jojo, Off Idejo Street. Victoria Island, Lagos, Nigeria.",
      phones: ["+234(0) 2012700403", "+234(0) 2012700404"],
      email: "",
    },
    {
      name: "Port Harcourt",
      address: "6 Fiddil Avenue Off Ordinance Junction Trans-Amadi, Port-Harcourt. Rivers State, Nigeria",
      phones: ["+234 (0) 8 465 2176"],
      email: "",
    },
    {
      name: "Pipe threading, valve assembly, composite pipe fabrication yard",
      address: "Plot H-04-003(B), Abuja Road (E6), Lekki Free Zone, South Quadrant. Lekki Free Zone, Ibeju-Lekki, Lagos, Nigeria.",
      phones: [],
      email: "enquiries@belloil.com",
    },
  ],
};

/* =========================================================
   IMAGE UPLOAD FIELD
========================================================= */
function ImageUploadField({ label, currentUrl, onUploaded }) {
  const [preview,   setPreview]   = useState(resolveUrl(currentUrl) || null);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState(null);
  const inputRef = useRef();

  useEffect(() => { setPreview(resolveUrl(currentUrl) || null); }, [currentUrl]);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res  = await fetch(UPLOAD_URL, { method: "POST", body: formData });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || "Upload failed");
      onUploaded(json.url);
      setPreview(resolveUrl(json.url));
    } catch (err) {
      setError("Upload failed. Try again.");
    } finally { setUploading(false); }
  };

  const handleRemove = () => {
    setPreview(null);
    onUploaded("");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="editor-img-field">
      <label>{label}</label>
      {preview ? (
        <div className="editor-img-preview-wrap">
          <img src={preview} alt="preview" className="editor-img-preview" />
          {uploading && <div className="editor-img-uploading"><div className="editor-loading__spinner editor-loading__spinner--sm" /><span>Uploading...</span></div>}
          {!uploading && (
            <div className="editor-img-actions">
              <button type="button" className="editor-img-btn editor-img-btn--change" onClick={() => inputRef.current?.click()}>Change</button>
              <button type="button" className="editor-img-btn editor-img-btn--remove" onClick={handleRemove}>Remove</button>
            </div>
          )}
        </div>
      ) : (
        <button type="button" className="editor-img-dropzone" onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading
            ? <><div className="editor-loading__spinner editor-loading__spinner--sm" /><span>Uploading...</span></>
            : <><span className="editor-img-dropzone__icon">🖼️</span><span>Click to upload image</span><span className="editor-img-dropzone__hint">JPG, PNG, WEBP — max 10MB</span></>
          }
        </button>
      )}
      {error && <p className="editor-img-error">{error}</p>}
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
    </div>
  );
}

/* =========================================================
   EDIT MODAL
========================================================= */
function ContactEditModal({ data, onSave, onClose, saving }) {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(data)));

  /* Office helpers */
  const setOfficeField = (oi, field, val) =>
    setDraft(d => {
      const offices = [...d.offices];
      offices[oi] = { ...offices[oi], [field]: val };
      return { ...d, offices };
    });

  const setPhone = (oi, pi, val) =>
    setDraft(d => {
      const offices = [...d.offices];
      const phones  = [...offices[oi].phones];
      phones[pi]    = val;
      offices[oi]   = { ...offices[oi], phones };
      return { ...d, offices };
    });

  const addPhone = (oi) =>
    setDraft(d => {
      const offices = [...d.offices];
      offices[oi]   = { ...offices[oi], phones: [...offices[oi].phones, ""] };
      return { ...d, offices };
    });

  const removePhone = (oi, pi) =>
    setDraft(d => {
      const offices = [...d.offices];
      offices[oi]   = { ...offices[oi], phones: offices[oi].phones.filter((_, i) => i !== pi) };
      return { ...d, offices };
    });

  const addOffice = () =>
    setDraft(d => ({
      ...d,
      offices: [...d.offices, { name: "New Office", address: "", phones: [], email: "" }],
    }));

  const removeOffice = (oi) =>
    setDraft(d => ({ ...d, offices: d.offices.filter((_, i) => i !== oi) }));

  return (
    <div className="editor-overlay" onClick={onClose}>
      <div className="editor-modal" onClick={e => e.stopPropagation()}>

        <div className="editor-modal__header">
          <h2>Edit Contact Page</h2>
          <button className="editor-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="editor-modal__body">

          {/* HERO IMAGE */}
          <p className="editor-section__title">Hero Image</p>
          <div className="editor-group">
            <ImageUploadField
              label="Page Banner Image"
              currentUrl={draft.heroImage}
              onUploaded={url => setDraft(d => ({ ...d, heroImage: url }))}
            />
          </div>

          {/* FORM TITLE */}
          <p className="editor-section__title">Contact Form</p>
          <div className="editor-group">
            <label>Form Title</label>
            <input
              value={draft.formTitle}
              onChange={e => setDraft(d => ({ ...d, formTitle: e.target.value }))}
            />
          </div>

          {/* OFFICES */}
          <p className="editor-section__title">Office Locations</p>

          {draft.offices.map((office, oi) => (
            <div key={oi} className="editor-group">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <p className="editor-group__label">Office {oi + 1}</p>
                {draft.offices.length > 1 && (
                  <button className="editor-value-row__remove" onClick={() => removeOffice(oi)} title="Remove office">✕</button>
                )}
              </div>

              <label>Office Name</label>
              <input
                value={office.name}
                onChange={e => setOfficeField(oi, "name", e.target.value)}
              />

              <label>Address</label>
              <textarea
                rows={3}
                value={office.address}
                onChange={e => setOfficeField(oi, "address", e.target.value)}
              />

              <label>Email</label>
              <input
                type="email"
                value={office.email}
                onChange={e => setOfficeField(oi, "email", e.target.value)}
                placeholder="Leave blank if none"
              />

              <label>Phone Numbers</label>
              {office.phones.map((phone, pi) => (
                <div key={pi} className="editor-value-row">
                  <input
                    value={phone}
                    onChange={e => setPhone(oi, pi, e.target.value)}
                    placeholder="+234..."
                  />
                  <button className="editor-value-row__remove" onClick={() => removePhone(oi, pi)} title="Remove">✕</button>
                </div>
              ))}
              <button className="editor-add-btn" onClick={() => addPhone(oi)}>+ Add Phone</button>
            </div>
          ))}

          <button className="editor-add-btn" onClick={addOffice}>+ Add Office</button>

        </div>

        <div className="editor-modal__footer">
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn--orange" onClick={() => onSave(draft)} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

      </div>
    </div>
  );
}

/* =========================================================
   MAIN EXPORT
========================================================= */
const AdminContact = () => {
  const [pageData,  setPageData]  = useState(DEFAULT_DATA);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [saveMsg,   setSaveMsg]   = useState(null);

  /* Contact form state */
  const [formData,     setFormData]     = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [formStatus,   setFormStatus]   = useState(null); // "sending" | "success" | "error"

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res  = await fetch(API_BASE);
      const json = await res.json();
      if (json.success) setPageData(json.data);
    } catch (err) { console.error("Fetch error:", err); }
  };

  const handleSave = async (draft) => {
    try {
      setSaving(true);
      const res  = await fetch(API_BASE, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(draft) });
      const json = await res.json();
      if (!json.success) throw new Error(json.message);
      await fetchData();
      setModalOpen(false);
      setSaveMsg("Changes saved successfully!");
    } catch (err) {
      setSaveMsg("Save failed. Please try again.");
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(null), 3500);
    }
  };

  /* Contact form handlers */
  const handleChange = (e) =>
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus("sending");
    try {
      const res  = await fetch(`${API_BASE}/submit`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(formData),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message);
      setFormStatus("success");
      setFormData({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      console.error("Form submit error:", err);
      setFormStatus("error");
    }
  };

  const heroBg = resolveUrl(pageData.heroImage) || Avatar;

  return (
    <>
      {/* Edit FAB */}
      <button className="edit-fab" onClick={() => setModalOpen(true)} title="Edit page content">
        <span className="edit-fab__icon">✏️</span>
        <span className="edit-fab__label">Edit Page</span>
      </button>

      {/* Toast */}
      {saveMsg && <div className="editor-toast">{saveMsg}</div>}

      {/* Modal */}
      {modalOpen && (
        <ContactEditModal
          data={pageData}
          onSave={handleSave}
          onClose={() => setModalOpen(false)}
          saving={saving}
        />
      )}

      {/* Hero */}
      <div className="img_carrier">
        <img src={heroBg} alt="" />
      </div>

      <section className="contact-section" aria-labelledby="contact-title">
        <div className="contact-section__header">
          <h2 className="contact-section__title" id="contact-title">Get In Touch</h2>
          <hr className="contact-section__divider" />
        </div>

        <hr className="contact-section__rule" />

        <div className="contact-section__body">

          {/* Info Panel */}
          <div className="contact-info">
            {pageData.offices.map((office) => (
              <div className="contact-info__office" key={office.name}>
                <h3 className="contact-info__office-name">{office.name}</h3>
                <p className="contact-info__address">{office.address}</p>
                {office.phones.length > 0 && (
                  <ul className="contact-info__phones">
                    {office.phones.map((phone, i) => (
                      <li key={i}>Tel {phone}</li>
                    ))}
                  </ul>
                )}
                {office.email && (
                  <div className="contact-info__email">
                    <span className="contact-info__email-label">Email:</span>
                    <a href={`mailto:${office.email}`} className="contact-info__email-value">
                      {office.email}
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Form Panel */}
          <div className="contact-form-panel">
            <h3 className="contact-form__title">{pageData.formTitle}</h3>

            {formStatus === "success" ? (
              <div style={{ padding: "24px", textAlign: "center", color: "#16a34a", fontWeight: 600, fontSize: "15px" }}>
                ✅ Message sent! We will get back to you shortly.
                <br />
                <button
                  onClick={() => setFormStatus(null)}
                  style={{ marginTop: "12px", background: "none", border: "1.5px solid #16a34a", color: "#16a34a", padding: "6px 16px", borderRadius: "6px", cursor: "pointer", fontWeight: 600 }}
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                <div className="contact-form__row">
                  <input className="contact-form__input" type="text"  name="name"    placeholder="Name"    value={formData.name}    onChange={handleChange} required />
                  <input className="contact-form__input" type="email" name="email"   placeholder="Email"   value={formData.email}   onChange={handleChange} required />
                </div>
                <div className="contact-form__row">
                  <input className="contact-form__input" type="tel"  name="phone"   placeholder="Phone"   value={formData.phone}   onChange={handleChange} />
                  <input className="contact-form__input" type="text" name="company" placeholder="Company" value={formData.company} onChange={handleChange} />
                </div>
                <div className="contact-form__field">
                  <textarea className="contact-form__textarea" name="message" placeholder="Message" value={formData.message} onChange={handleChange} required />
                </div>
                {formStatus === "error" && (
                  <p style={{ color: "#dc2626", fontSize: "13px", marginBottom: "8px" }}>
                    Something went wrong. Please try again.
                  </p>
                )}
                <button type="submit" className="contact-form__submit" disabled={formStatus === "sending"}>
                  {formStatus === "sending" ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </div>

        </div>
      </section>
    </>
  );
};

export default AdminContact;